import { datadogLogs } from '@datadog/browser-logs';
import { tracking } from '@iwoca/frontend-tracking-library';
import {
  fetchGetCompanySearch,
  fetchGetContextByStateKey,
  fetchGetOnlineAccount,
  fetchGetStateByStateKey,
  GetOnlineAccountResponse,
  GetStateByStateKeyResponse,
} from '@iwoca/lapi-client/edge';
import {
  fetchGetApiIntroducerPortalIntroducerState,
  fetchGetApiIntroducerPortalIntroducerApplicationOfferAllowedWithdrawalFeeRates,
  fetchGetApiIntroducerPortalIntroducerApplicationOfferDrawDownRepaymentSchedule,
  fetchGetApiIntroducerPortalIntroducerApplicationOfferFundingRequirements,
  fetchGetApiIntroducerPortalIntroducerApplicationOfferWithdrawalFeeRate,
  fetchGetApiIntroducerPortalIntroducerApplicationRejectOfferReasons,
  fetchGetApiIntroducerPortalRepaymentSimulator,
  fetchGetApiIntroducerPortalIntroducer,
  fetchGetApiIntroducerPortalDirectorSearch,
  GetApiIntroducerPortalRepaymentSimulatorResponse,
  fetchGetApiIntroducerPortalIntroducerApplicationOfferExpectedCommission,
  GetApiIntroducerPortalIntroducerApplicationOfferExpectedCommissionResponse,
  fetchGetApiIntroducerPortalIntroducerApplicationDeclineReason,
  GetApiIntroducerPortalIntroducerApplicationDeclineReasonResponse,
  fetchGetApiIntroducerPortalIntroducerApplication,
  fetchGetApiIntroducerPortalIntroducerApplicationCustomerContactUpdates,
  GetApiIntroducerPortalIntroducerApplicationCustomerContactUpdatesResponse,
  GetApiIntroducerPortalIntroducerApplicationOfferWithdrawalFeeRateResponse,
  GetApiIntroducerPortalIntroducerApplicationRejectOfferReasonsResponse,
  GetApiIntroducerPortalIntroducerStateResponse,
  GetApiIntroducerPortalIntroducerApplicationOfferDrawDownRepaymentScheduleResponse,
  GetApiIntroducerPortalIntroducerApplicationResponse,
  GetApiIntroducerPortalIntroducerApplicationOfferFundingRequirementsResponse,
  GetApiIntroducerPortalDirectorSearchResponse,
  fetchGetApiIntroducerPortalIntroducerDocument,
  GetApiIntroducerPortalIntroducerDocumentResponse,
} from '@iwoca/lapi-client/introducer_portal';
import { useQuery } from '@tanstack/react-query';
import { useRouterApplicationId } from 'components/Signup/hooks/useRouterApplicationId';
import {
  MAX_DE_AMOUNT,
  MAX_UK_AMOUNT,
  MAX_WITHDRAWAL_FEE,
  MIN_DE_AMOUNT,
  MIN_DE_CREDIT,
  MIN_REPAYMENT_AMOUNT,
  MIN_UK_AMOUNT,
  MIN_UK_CREDIT,
  MIN_WITHDRAWAL_FEE,
} from 'components/StandAloneCalculator/filterConfigs.helper';
import { getInterestConfig } from 'components/StandAloneCalculator/InterestRateFilter/InterestRateFilter';
import { useIntroducerData } from 'contexts/IntroducerData/IntroducerData.context';
import { addMonths } from 'date-fns';
import { useAmplitude } from 'hooks/useAmplitude';
import { useLocale } from 'hooks/useLocale';
import { usePathname } from 'hooks/usePathname/usePathname';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { IwQueryClient } from 'store/IwQueryClient';
import { TCalculatorProducts, TIntroducerPermission } from 'types';
import { APPLICATION_STATUS } from 'utils/applications/applications.config';
import { dateToString } from 'utils/common/common';
import { getCookie } from 'utils/cookieHelper';
import { DE_LOCALE, UK_LOCALE } from 'utils/i18n';
import { PATH_NAMES } from 'utils/routes/pathNames';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { TIntroducerApplication } from './applications.types';
import { TGetStateResponseData } from './lapiOverrides.types';
import {
  lendingAPIGetDocuments,
  getIntroducerContext,
  fetchGetIntroducerPortalApiOffers,
} from './lendingApi';
import {
  TGetAllowedWithdrawalFeesResponse,
  TGetContextByStateKeyResponse,
  TGetDocumentsResponse,
  TGetIntroducerContextResponse,
  TTermLength,
} from './lendingApi.types';

export const useQueryContext = () => {
  const isLoggedIn = getCookie('isLoggedIn') === 'True';
  const pathname = usePathname();
  const [searchParams] = useSearchParams();
  const impersonate = searchParams.get('__impersonate');

  return useQuery<TGetIntroducerContextResponse>({
    queryKey: ['context', impersonate],
    queryFn: () => getIntroducerContext(impersonate),
    enabled:
      !pathname.startsWith(PATH_NAMES.register()) &&
      !(pathname === PATH_NAMES.calculator() && !isLoggedIn),
  });
};

export const useQueryIntroducer = () => {
  const navigate = useNavigate();
  const context = useQueryContext();
  const introducerId = context.data?.data.introducer_id;

  if (introducerId && typeof window !== 'undefined') {
    datadogLogs.setGlobalContextProperty(
      'introducer_id',
      introducerId.toString(),
    );
  }

  const queryFn = async () => {
    const response = await fetchGetApiIntroducerPortalIntroducer({
      introducerId: introducerId as string,
    });

    if (!response.data?.introducer.is_active) {
      navigate(PATH_NAMES.forbidden());
    }

    return response;
  };

  const data = useQuery({
    queryKey: ['introducer', introducerId],
    queryFn,
    enabled: !!introducerId,
  });

  return {
    ...data,
    contextQuery: context,
    checkPermission: (permission: TIntroducerPermission) =>
      data.data?.data.introducer.permissions[permission],
  };
};

export function useGetIntroducerApplication() {
  const { introducerId } = useIntroducerData();
  const applicationId = useRouterApplicationId();

  const fetchApplicationFunc = async () => {
    return await fetchGetApiIntroducerPortalIntroducerApplication({
      introducerId: introducerId as string,
      applicationId: applicationId as string,
    });
  };

  const { data, status, refetch, isLoading, isFetching } =
    useQuery<GetApiIntroducerPortalIntroducerApplicationResponse>({
      queryKey: ['fetchGetApplication', introducerId, applicationId],
      queryFn: () => fetchApplicationFunc(),
      enabled: Boolean(introducerId && applicationId),
    });

  return {
    application: data?.data,
    refetchApplication: refetch,
    applicationStatus: status,
    isLoading,
    isFetching,
  };
}

export function useGetSignupState<TResponse = TGetStateResponseData>() {
  const { application } = useGetIntroducerApplication();
  const stateKey = application?.customer?.state_key;

  const { data, status, refetch, isLoading } =
    useQuery<GetStateByStateKeyResponse>({
      queryKey: ['fetchGetState', stateKey],
      queryFn: () => fetchGetStateByStateKey({ stateKey: stateKey as string }),
      enabled: Boolean(stateKey),
    });

  return {
    state: data?.data as TResponse | undefined,
    refetchState: refetch,
    stateStatus: status,
    stateFetching: isLoading,
  };
}

export const useGetState = () => {
  const { introducerId } = useIntroducerData();
  const { application } = useGetIntroducerApplication();
  const stateKey = application?.customer?.state_key;

  const { data, status, refetch, isLoading } =
    useQuery<GetApiIntroducerPortalIntroducerStateResponse>({
      queryKey: ['fetchGetState', stateKey, introducerId],
      queryFn: () =>
        fetchGetApiIntroducerPortalIntroducerState({
          introducerId: introducerId as string,
          stateKey: stateKey as string,
        }),
      enabled: Boolean(stateKey) && Boolean(introducerId),
    });

  return {
    state: data?.data,
    refetchState: refetch,
    stateStatus: status,
    stateFetching: isLoading,
  };
};

export const useGetDocuments = (state_key?: string) => {
  return useQuery<TGetDocumentsResponse>({
    queryKey: ['documents', state_key],
    queryFn: () => lendingAPIGetDocuments(state_key as string),
    enabled: !!state_key,
  });
};

export const useGetIntroducerDocuments = (introducerId: string | null) => {
  return useQuery<GetApiIntroducerPortalIntroducerDocumentResponse>({
    queryKey: ['introducerDocuments', introducerId],
    queryFn: () =>
      fetchGetApiIntroducerPortalIntroducerDocument({
        introducerId: introducerId as string,
      }),
    enabled: Boolean(introducerId),
  });
};

export const useGetOffers = (enabled: boolean, applicationId?: string) => {
  const { introducerId } = useIntroducerData();

  const fetchGetApplicationsOffersWithApplicationInvalidation = async () => {
    const response = await fetchGetIntroducerPortalApiOffers(
      introducerId as string,
      applicationId as string,
    );

    if (response) {
      IwQueryClient.getOrCreate().invalidateQueries({
        queryKey: ['fetchGetApplication'],
      });
    }

    return response;
  };

  return useQuery({
    queryKey: ['offers', applicationId, introducerId],
    queryFn: () => fetchGetApplicationsOffersWithApplicationInvalidation(),
    enabled: Boolean(introducerId) && Boolean(applicationId) && enabled,
  });
};

export const useGetIntroducerFundingRequirements = () => {
  const { application } = useGetIntroducerApplication();
  const { introducerId } = useIntroducerData();

  const formalOffers = application?.formal_offer_sets[0].formal_offers;

  const applicationId = application?.application_uid as string;
  const offerId =
    formalOffers?.find((item) => item.is_selected)?.offer_id ||
    (formalOffers?.[0].offer_id as string);

  const queryFn = async () =>
    await fetchGetApiIntroducerPortalIntroducerApplicationOfferFundingRequirements(
      { introducerId: introducerId as string, applicationId, offerId },
    );

  return useQuery<GetApiIntroducerPortalIntroducerApplicationOfferFundingRequirementsResponse>(
    {
      queryKey: ['fundingRequirements', introducerId, applicationId, offerId],
      queryFn,
      enabled:
        Boolean(introducerId) && Boolean(applicationId) && Boolean(offerId),
    },
  );
};

export const useGetCustomerContactUpdates = () => {
  const { introducerId } = useIntroducerData();
  const { application } = useGetIntroducerApplication();
  const applicationId = application?.application_uid;

  const queryFn = async () =>
    await fetchGetApiIntroducerPortalIntroducerApplicationCustomerContactUpdates(
      {
        introducerId: introducerId as string,
        applicationId: applicationId as string,
      },
    );

  return useQuery<GetApiIntroducerPortalIntroducerApplicationCustomerContactUpdatesResponse>(
    {
      queryKey: ['contactUpdates', introducerId, applicationId],
      queryFn,
      enabled: Boolean(introducerId) && Boolean(applicationId),
    },
  );
};

export const useGetWithdrawalFeeRate = (offerId: string) => {
  const { application } = useGetIntroducerApplication();
  const { introducerId } = useIntroducerData();
  const applicationId = application?.application_uid as string;

  const queryFn = async () =>
    await fetchGetApiIntroducerPortalIntroducerApplicationOfferWithdrawalFeeRate(
      { introducerId: introducerId as string, applicationId, offerId },
    );

  return useQuery<GetApiIntroducerPortalIntroducerApplicationOfferWithdrawalFeeRateResponse>(
    {
      queryKey: ['withdrawalFeeRate', applicationId, offerId],
      queryFn,
      enabled: !!applicationId && !!introducerId,
    },
  );
};

export const useGetAllowedWithdrawalFee = (
  introducerId: string,
  applicationId: string,
  offerId: string,
  productType: string,
) => {
  const queryFn = async () =>
    await fetchGetApiIntroducerPortalIntroducerApplicationOfferAllowedWithdrawalFeeRates(
      { introducerId, applicationId, offerId },
    );

  return useQuery<TGetAllowedWithdrawalFeesResponse>({
    queryKey: ['allowedWithdrawalFees', offerId],
    queryFn,
    enabled:
      !!applicationId &&
      !!introducerId &&
      !!offerId &&
      (productType === 'flexi24' || productType === 'credit_facility'),
  });
};

export const useGetDirectorSearch = (companyNumber?: string) => {
  const queryFn = async () =>
    await fetchGetApiIntroducerPortalDirectorSearch({
      companyNumber: companyNumber as string,
    });

  return useQuery<GetApiIntroducerPortalDirectorSearchResponse>({
    queryKey: ['directorSearch', companyNumber],
    queryFn,
    enabled: Boolean(companyNumber),
  });
};

export const useGetRejectOfferReasons = (
  introducerId?: string | null,
  applicationId?: string,
  applicationStatus?: string,
) => {
  const queryFn = async () =>
    await fetchGetApiIntroducerPortalIntroducerApplicationRejectOfferReasons({
      introducerId: introducerId as string,
      applicationId: applicationId as string,
    });

  return useQuery<GetApiIntroducerPortalIntroducerApplicationRejectOfferReasonsResponse>(
    {
      queryKey: ['rejectOfferReasons', applicationId],
      queryFn,
      enabled:
        Boolean(applicationId) &&
        Boolean(introducerId) &&
        applicationStatus === 'offer_rejected',
    },
  );
};

export const useGetDeclineReasons = (
  introducerId: string | null,
  applicationId?: string,
  applicationStatus?: TIntroducerApplication['status'],
) => {
  const queryFn = async () =>
    await fetchGetApiIntroducerPortalIntroducerApplicationDeclineReason({
      introducerId: introducerId as string,
      applicationId: applicationId as string,
    });

  return useQuery<GetApiIntroducerPortalIntroducerApplicationDeclineReasonResponse>(
    {
      queryKey: ['declineReasons', applicationId],
      queryFn,
      enabled:
        Boolean(applicationId) &&
        Boolean(introducerId) &&
        applicationStatus === APPLICATION_STATUS.declined,
    },
  );
};

// Drawdown early repayment calculator schedule
export const useGetDrawdownRepaymentSchedule = ({
  amount,
  formalOffer,
  isRecalculatingMinimum,
  date,
  overpaymentAmount,
  minimumMonthlyRepayment,
}: {
  amount: number;
  formalOffer: TIntroducerApplication['formal_offer_sets'][number]['formal_offers'][number];
  isRecalculatingMinimum: boolean;
  date?: string;
  overpaymentAmount?: number;
  minimumMonthlyRepayment?: number | null;
}) => {
  const { introducerId } = useIntroducerData();
  const { application } = useGetIntroducerApplication();
  const { isDE } = useLocale();
  const applicationId = application?.application_uid as string;
  const stateKey = application?.customer?.state_key;
  const offerId = formalOffer.offer_id;

  const trimmedMonthlyRepayment =
    minimumMonthlyRepayment && Number(minimumMonthlyRepayment.toFixed(2));

  const queryFn = async () =>
    await fetchGetApiIntroducerPortalIntroducerApplicationOfferDrawDownRepaymentSchedule(
      {
        introducerId: introducerId as string,
        applicationId,
        offerId,
        query: {
          amount,
          early_final_repayment_date: date,
          overpayment_repayment_amount: overpaymentAmount,
        },
      },
    );

  const isValidAmount =
    amount >= (isDE ? MIN_DE_CREDIT : MIN_UK_CREDIT) &&
    amount <= formalOffer.max_amount;

  const isValidOverpayment =
    (!overpaymentAmount && overpaymentAmount !== 0) ||
    overpaymentAmount > Number(trimmedMonthlyRepayment);

  return useQuery<GetApiIntroducerPortalIntroducerApplicationOfferDrawDownRepaymentScheduleResponse>(
    {
      queryKey: [
        'drawdownRepaymentSchedule',
        amount,
        date,
        offerId,
        overpaymentAmount,
        trimmedMonthlyRepayment,
      ],
      queryFn,
      enabled:
        Boolean(stateKey) &&
        Boolean(formalOffer) &&
        isValidAmount &&
        isValidOverpayment &&
        Boolean(trimmedMonthlyRepayment) &&
        !isRecalculatingMinimum,
    },
  );
};

// Stand alone calculator schedule
export const useGetRepaymentSimulator = ({
  isDE,
  product,
  interestRate,
  amount,
  duration,
  earlyRepaymentStartDate,
  earlyRepaymentEndDate,
  withdrawalFee,
  overpaymentAmount,
  maxTermLength,
  queryKey,
}: {
  isDE: boolean;
  product: TCalculatorProducts;
  interestRate: string;
  amount: string;
  duration: string;
  earlyRepaymentStartDate: string | null;
  earlyRepaymentEndDate: string | null;
  withdrawalFee?: string;
  maxTermLength: TTermLength;
  overpaymentAmount?: string;
  queryKey: (string | number | null | undefined)[];
}) => {
  interestRate = interestRate.replace(',', '.');
  const { trackEvent } = useAmplitude();

  // An unlikely occurence but this prevents errors where start date is in the past
  const todaysDate = dateToString(new Date());
  const validStartDate = earlyRepaymentStartDate
    ? earlyRepaymentStartDate < todaysDate
      ? todaysDate
      : earlyRepaymentStartDate
    : todaysDate;

  // If there is no overpayment amount, use earlyRepaymentEndDate.
  // If earlyRepaymentEndDate is not set, default to the current date plus maxTermLength months.
  // If an overpayment amount exists, set earlyFinalRepaymentDate to undefined.
  const earlyFinalRepaymentDate = !overpaymentAmount
    ? earlyRepaymentEndDate ||
      dateToString(addMonths(new Date(), maxTermLength))
    : undefined;

  const interestRateConfig = getInterestConfig(
    isDE ? DE_LOCALE : UK_LOCALE,
    maxTermLength,
  );

  const trimmedFundingFee = (Number(withdrawalFee) / 100).toFixed(4);
  const trimmedInterestRate = (Number(interestRate) / 100).toFixed(4);

  trackEvent(AMPLITUDE_EVENTS.calculatorFilterChanged, {
    Product: product,
    'Loan amount': amount,
    'Interest rate': interestRate,
    'Withdrawal fee': withdrawalFee || null,
    'Number of repayments': duration,
  });

  const queryFn = async () =>
    await fetchGetApiIntroducerPortalRepaymentSimulator({
      query: {
        product,
        // @ts-ignore LAPI expects number but in query string so string fine
        initial_principal: amount,
        // @ts-ignore LAPI expects number but in query string so string fine
        '30_day_rate': trimmedInterestRate,
        loan_duration_months: maxTermLength,
        funding_fee: Number(trimmedFundingFee),
        start_date: validStartDate,
        early_final_repayment_date: earlyFinalRepaymentDate,
        overpayment_repayment_amount: Number(overpaymentAmount) || undefined,
        promotions: undefined,
      },
    });

  const loanAmountIsValid =
    Number(amount) >= (isDE ? Number(MIN_DE_AMOUNT) : Number(MIN_UK_AMOUNT)) &&
    Number(amount) <= (isDE ? Number(MAX_DE_AMOUNT) : Number(MAX_UK_AMOUNT));

  const interestRateIsValid =
    Number(interestRate) >= interestRateConfig.min &&
    Number(interestRate) <= interestRateConfig.max;

  const withdrawalFeeIsValid =
    Number(withdrawalFee) >= Number(MIN_WITHDRAWAL_FEE) &&
    Number(withdrawalFee) <= Number(MAX_WITHDRAWAL_FEE);

  const overpaymentIsValid =
    Number(overpaymentAmount) >= Number(MIN_REPAYMENT_AMOUNT) &&
    Number(overpaymentAmount) <=
      (isDE ? Number(MAX_DE_AMOUNT) : Number(MAX_UK_AMOUNT));

  return useQuery<GetApiIntroducerPortalRepaymentSimulatorResponse>({
    queryKey: ['repaymentSimulator', queryKey],
    queryFn,
    enabled:
      loanAmountIsValid &&
      interestRateIsValid &&
      (!overpaymentAmount || overpaymentIsValid) &&
      (isDE || withdrawalFeeIsValid),
    staleTime: 3600000, // 1 hour
  });
};

export const useIntroducerContactEmail = () => {
  const { isDE } = useLocale();
  const [searchParams] = useSearchParams();

  if (isDE) {
    return 'partner@iwoca.de';
  }
  if (searchParams.get('introducerType') === 'accountant') {
    return 'accountants@iwoca.co.uk';
  }
  return 'brokers@iwoca.co.uk';
};

export const useGetExpectedCommissions = (
  introducerId?: string | null,
  applicationId?: string,
  offerId?: string,
  showExpectedCommission: boolean = true,
) => {
  const queryFn = async () =>
    await fetchGetApiIntroducerPortalIntroducerApplicationOfferExpectedCommission(
      {
        introducerId: introducerId as string,
        applicationId: applicationId as string,
        offerId: offerId as string,
      },
    );

  return useQuery<GetApiIntroducerPortalIntroducerApplicationOfferExpectedCommissionResponse>(
    {
      queryKey: ['amount', applicationId, offerId],
      queryFn,
      enabled:
        Boolean(applicationId) &&
        Boolean(introducerId) &&
        Boolean(offerId) &&
        showExpectedCommission,
    },
  );
};

export const useGetStateContext = (stateKey?: string) => {
  return useQuery({
    queryKey: ['state-context', stateKey],
    queryFn: () =>
      fetchGetContextByStateKey({
        stateKey: stateKey as string,
        // TODO: Fix type when edge schema is correct and update to use lapi-client
      }) as TGetContextByStateKeyResponse,
    enabled: Boolean(stateKey),
  });
};

export const useGetOnlineAccounts = (stateKey?: string) => {
  return useQuery<GetOnlineAccountResponse>({
    queryKey: ['online-account', stateKey],
    queryFn: () => fetchGetOnlineAccount({ stateKey: stateKey as string }),
    enabled: Boolean(stateKey),
  });
};

export function useGetCompanySearch({
  searchQuery,
  enabled,
}: {
  searchQuery: string;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: ['companySearch', searchQuery],
    queryFn: async () => {
      tracking.ampli.companySearchUsed({
        query_length: searchQuery.length,
      });

      return await fetchGetCompanySearch({
        query: { company_name: searchQuery },
      });
    },
    // @ts-expect-error LAPI client uses UK typing, overriding to DE
    select: (data) => data as TGetCompanySearchResponseDE,
    enabled: !!searchQuery && enabled,
  });
}

// RCF repayment summary — simpler version of useGetDrawdownRepaymentSchedule
// that doesn't require minimumMonthlyRepayment, used for the RCF column preview
export const useGetRCFRepaymentSummary = (
  offerId: string,
  maxAmount: number,
) => {
  const { introducerId } = useIntroducerData();
  const { application } = useGetIntroducerApplication();
  const applicationId = application?.application_uid as string;

  return useQuery<GetApiIntroducerPortalIntroducerApplicationOfferDrawDownRepaymentScheduleResponse>(
    {
      queryKey: [
        'rcfRepaymentSummary',
        offerId,
        maxAmount,
        introducerId,
        applicationId,
      ],
      queryFn: () =>
        fetchGetApiIntroducerPortalIntroducerApplicationOfferDrawDownRepaymentSchedule(
          {
            introducerId: introducerId as string,
            applicationId,
            offerId,
            query: { amount: maxAmount },
          },
        ),
      enabled:
        Boolean(introducerId) &&
        Boolean(applicationId) &&
        Boolean(offerId) &&
        maxAmount > 0,
    },
  );
};
